import { Fragment, useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { XMarkIcon } from "@heroicons/react/24/solid";
import CheckoutForm from "../components/CheckoutForm";
import { Dialog, Transition } from "@headlessui/react";
import { useLoggedContext } from "../context/provider";
import { STRIPE_KEY } from "../utils/config";
import { use } from "react";

const ChangeCardModal = ({ isOpen, setIsOpen, getProfile, selected, teamSize, updatePlan, changeCard, onSuccess }) => {
  // Context
  const { setBtnLoading, user } = useLoggedContext();

  // Close Modal
  const closeModal = () => {
    setIsOpen(false);
    getProfile();
    setBtnLoading(false);
    onSuccess();
  };

  // Stripe Details
  const stripePromise = loadStripe(STRIPE_KEY);
  
  
  // Calculate layout classes
  const dialogPanelClasses = `w-full ${changeCard ? "max-w-md" : "h-[77vh] 3xl:h-[72vh] max-w-[60rem]"} overflow-hidden rounded-2xl bg-white p-8 align-middle shadow-xl transition-all`;
  const gridClasses = `grid ${changeCard ? "grid-cols-1" : "grid-cols-2"} gap-x-6 pt-6`;
  
  // Calculate prices
  const isEnterprise = selected?.name?.toLowerCase() === "enterprise";
  const isYearly = selected?.type?.toLowerCase() === "yearly";
  const hasDiscount = isYearly && selected?.discount_amount > 0;
  
  // Calculate trial end date and determine if free trial exists
  const hasTrial = selected?.free_trial_days > 0;
  const currentDate = new Date();
  const trialEndDate = new Date(currentDate.getTime() + (selected?.free_trial_days || 0) * 24 * 60 * 60 * 1000);
  const formattedTrialEndDate = trialEndDate.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  
  // Base prices
  const basePrice = isEnterprise  ? (teamSize * (Number(selected?.per_user) || 0))  : (Number(selected?.price) + selected?.discount_amount || 0);
  const formattedBasePrice = basePrice.toFixed(2);
  
  // Discount amount
  const discountAmount = hasDiscount ? selected?.discount_amount : 0;
  const formattedDiscountAmount = discountAmount.toFixed(2);
  
  // Final price
  const finalPrice = hasDiscount ? basePrice - discountAmount : basePrice;
  const formattedFinalPrice = finalPrice.toFixed(2);
  
  // Trial period text - only created if trial days exist
  const trialDaysText = hasTrial ? `${selected?.free_trial_days}-${selected?.free_trial_days > 1 ? "days" : "day"}` : "";
  
  // Nursery details
  const nurseryName = user?.user?.nursery[0]?.name || "N/A";
  const nurseryAddress = user?.user?.nursery[0]?.address || "--";
  const nurseryCountry = user?.user?.nursery[0]?.country || "--";
  
  return (
    <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-75"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-75"
              >
                <Dialog.Panel className={dialogPanelClasses}>
                  <div className="flex items-center justify-between">
                    <Dialog.Title as="h3" className="font-poppins text-2xl font-bold leading-7 text-secondary-color">
                      Checkout
                    </Dialog.Title>

                    {/* <XMarkIcon onClick={closeModal} className="h-8 w-8 cursor-pointer text-primary-color-200" /> */}
                  </div>
                  <div className={gridClasses}>
                    {!changeCard && (
                      <div className="bg-primary-color-600 max-h-[90%] rounded p-3">
                        <h3 className="text-lg font-semibold text-secondary-color">Order Summary</h3>
                        <div className="overflow-hidden border-b border-dashed">
                          <table className="w-full">
                            <thead className="border-b border-dashed">
                              <tr>
                                <th className="px-2 py-3 text-left text-base font-medium text-primary-color-400">Plan</th>
                                <th className="px-2 py-3 text-right text-base font-medium text-primary-color-400">Billing Cycle</th>
                                <th className="px-2 py-3 text-right text-base font-medium text-primary-color-400">Seats</th>
                                <th className="px-2 py-3 text-right text-base font-medium text-primary-color-400">Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="whitespace-nowrap px-2 py-4 text-base text-secondary-color">{selected?.name}</td>
                                <td className="whitespace-nowrap px-2 py-4 text-right text-base text-secondary-color">{selected?.type}</td>
                                <td className="whitespace-nowrap px-2 py-4 text-right text-base text-secondary-color">{teamSize}</td>
                                <td className="whitespace-nowrap px-2 py-4 text-right text-base text-secondary-color">
                                  £{formattedBasePrice}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="border-b border-dashed pb-4 pt-2">
                          <div className="flex items-center justify-between pt-2">
                            <h2 className="text-base font-medium uppercase italic text-secondary-color">SubTotal</h2>
                            <h2 className="text-base font-medium text-secondary-color">
                              £{formattedBasePrice}
                            </h2>
                          </div>
                          {hasDiscount && (
                            <div className="flex items-center justify-between pt-2">
                              <h2 className="text-base font-medium italic text-green-600">Discount</h2>
                              <h2 className="text-base font-medium text-green-600">-£{formattedDiscountAmount}</h2>
                            </div>
                          )}
                          <div className="flex items-center justify-between pt-2">
                            <h2 className="text-base font-medium italic text-secondary-color">Tax</h2>
                            <h2 className="text-base font-medium text-secondary-color">£0.00</h2>
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-2">
                          <h2 className="text-2xl font-semibold text-secondary">Total</h2>
                          <h2 className="text-2xl font-semibold text-secondary">
                            £{formattedFinalPrice}
                          </h2>
                        </div>
                        <p className="pt-6 text-base font-bold text-red-500">
                          {hasTrial ? (
                            <>
                              {`You will be charged`}{" "}
                              <span className="mr-1 bg-yellow-200 px-1 text-lg text-secondary">
                                £{formattedFinalPrice}
                              </span>
                              after your{" "}
                              <span className="bg-yellow-200 px-1 text-lg text-secondary">{` ${trialDaysText}`}</span>{" "}
                              {` free trial ends on `} <span className="bg-yellow-200 px-1 text-lg text-secondary">{`${formattedTrialEndDate}`}</span>
                            </>
                          ) : (
                            <>
                              {`You will be charged`}{" "}
                              <span className="mr-1 bg-yellow-200 px-1 text-lg text-secondary">
                                £{formattedFinalPrice}
                              </span>
                              {`on `} <span className="bg-yellow-200 px-1 text-lg text-secondary">{`${formattedTrialEndDate}`}</span>
                            </>
                          )}
                        </p>
                      </div>
                    )}
                    <div>
                      {!changeCard && (
                        <div className="rounded border px-3 py-2">
                          <h3 className="text-lg font-semibold text-secondary-color">Billing Information</h3>
                          <div>
                            <h2 className="text-base font-medium text-black/70">{nurseryName}</h2>
                            <p className="text-sm font-medium text-primary-color-400">
                              {nurseryAddress}, {nurseryCountry}
                            </p>
                          </div>
                        </div>
                      )}
                      <Elements stripe={stripePromise}>
                        <CheckoutForm
                          selected={selected}
                          closeModal={closeModal}
                          getProfile={getProfile}
                          teamSize={teamSize}
                          updatePlan={updatePlan}
                          changeCard={changeCard}
                        />
                      </Elements>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
  );
};

export default ChangeCardModal;
